import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Field } from '..';
import { mapFieldToProps, mapValuesToPayload } from '../utils';
import http from '../../../utils/http';

const EMPTY_DEPS: string[] = [];

function normalizeDependencySelectOptionsResponse(data: unknown) {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((item: any) => {
    if (
      item &&
      typeof item === 'object' &&
      'value' in item &&
      'label' in item
    ) {
      return { value: item.value, label: item.label };
    }

    if (item && typeof item === 'object' && 'id' in item) {
      return {
        value: item.id,
        label: item.name ?? item.label ?? String(item.id),
      };
    }

    return { value: String(item), label: String(item) };
  });
}

function buildDepsQueryParams(
  deps: string[],
  values: Record<string, any>,
): Record<string, any> {
  const params: Record<string, any> = {};

  for (const name of deps) {
    const v = values[name];
    if (v === undefined || v === null) {
      continue;
    }

    params[name] = v;
  }

  return params;
}

export function useIntelligenceLayerFieldProps(field: Field) {
  const { control } = useFormContext();
  const formValues = useWatch({ control });
  const values = useMemo(
    () => mapValuesToPayload(formValues ?? {}),
    [formValues],
  );

  const isSelect = field.type === 'select';
  const legacyDependency = isSelect ? field.settings.dependency : undefined;
  const depsList = useMemo(() => {
    if (!isSelect || !Array.isArray(field.settings.deps)) {
      return EMPTY_DEPS;
    }

    return field.settings.deps;
  }, [isSelect, field.settings.deps]);
  const optionsUrl = field.settings.optionsUrl as string | undefined;
  const staticOptions = field.settings.options ?? [];

  const useServerOptions = Boolean(
    isSelect && !legacyDependency && depsList.length > 0 && optionsUrl,
  );

  const depValuesForKey = depsList.map((name) => values[name]);

  const optionsQuery = useQuery({
    queryKey: [
      'intelligence-field-select-options',
      field.name,
      field.id,
      'deps',
      depsList,
      depValuesForKey,
      optionsUrl,
    ],
    queryFn: async () => {
      const params = buildDepsQueryParams(depsList, values);
      const response = await http.get(optionsUrl!, { params });

      return normalizeDependencySelectOptionsResponse(response);
    },
    enabled: useServerOptions,
    placeholderData: keepPreviousData,
  });

  const fieldProps = useMemo(() => {
    const props = mapFieldToProps(field);

    if (field.type !== 'select') {
      return props;
    }

    if (legacyDependency) {
      const dep = legacyDependency;

      return {
        ...props,
        // @ts-ignore
        options: (props.options ?? []).filter((item: any) => {
          if (typeof item[`${dep}Id`] === 'undefined') {
            return true;
          }

          if (Array.isArray(values[dep] ?? null)) {
            return values[dep].includes(+item[`${dep}Id`]);
          }

          return +item[`${dep}Id`] === +values[dep];
        }),
      };
    }

    if (depsList.length > 0 && optionsUrl) {
      return {
        ...props,
        options:
          optionsQuery.data !== undefined ? optionsQuery.data : staticOptions,
      };
    }

    return props;
  }, [
    field,
    depsList,
    legacyDependency,
    optionsQuery.data,
    optionsUrl,
    staticOptions,
    values,
  ]);

  return { fieldProps, values };
}
